import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo, useEffect, createRef } from 'react';
import { MdError, MdExpandMore, MdExpandLess, MdClose, MdCheck } from 'react-icons/md';
import cx from 'classnames';
import { ParsedError, parseErrors } from '../../utils/graphQLErrors';
import Button from './Button';

// Types for request management
export interface RequestState {
  id: string;
  title?: string;
  isBlocking: boolean;
  isLoading: boolean;
  isCompleted: boolean;
  promise: Promise<any>;
  errors?: ParsedError[];
  createdAt: Date;
  completedAt?: Date;
}

export interface RequestContextValue {
  trackRequest: <T>(
    promise: Promise<T>,
    options: {
      title?: string;
      isBlocking?: boolean;
    }
  ) => Promise<T>;
  dismissRequest: (id: string) => void;
  getActiveRequests: () => RequestState[];
  getBlockingRequests: () => RequestState[];
  getSmallRequests: () => RequestState[];
}

// Context for request management
const RequestContext = createContext<RequestContextValue | null>(null);

export const RequestManagerContextRef: { current: RequestContextValue | null } = {
  current: null,
};

// Hook to use request context
export const useRequestManager = () => {
  const context = useContext(RequestContext);
  if (!context) {
    throw new Error('useRequestManager must be used within a RequestManagerProvider');
  }
  return context;
};

// Provider component
export const RequestManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [requests, setRequests] = useState<Map<string, RequestState>>(new Map([]));

  // Handle ESC key to dismiss all requests
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only dismiss requests if there are blocking requests
        const hasBlockingRequests = Array.from(requests.values()).some(req => req.isBlocking);
        if (hasBlockingRequests) {
          setRequests(new Map());
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [requests]);

  const trackRequest = useCallback(function trackRequest<T>(
    promise: Promise<T>,
    options: {
      title?: string;
      isBlocking?: boolean;
    }
  ): Promise<T> {
    const id = Math.random().toString(36).substr(2, 9);
    const newRequest: RequestState = {
      id,
      title: options.title || 'Processing...',
      isBlocking: options.isBlocking || false,
      isLoading: true,
      isCompleted: false,
      promise,
      createdAt: new Date(),
    };

    // Add request to state
    setRequests(prev => new Map(prev).set(id, newRequest));
    
    // Handle promise resolution/rejection
    return promise
      .then((result) => {
        // Request completed successfully - show success state briefly
        setRequests(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(id);
          if (existing) {
            newMap.set(id, {
              ...existing,
              isLoading: false,
              isCompleted: true,
              completedAt: new Date()
            });
          }
          return newMap;
        });
        
        // Remove the request after showing success state for 2 seconds
        setTimeout(() => {
          setRequests(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
          });
        }, 2000);
        
        return result;
      })
      .catch((error) => {
        // Request failed - set error state
        setRequests(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(id);
          if (existing) {
            newMap.set(id, {
              ...existing,
              errors: parseErrors(error),
              isLoading: false,
              isCompleted: false
            });
          }
          return newMap;
        });
        throw error; // Re-throw so the calling code can still handle the error
      });
  }, []);

  const dismissRequest = useCallback((id: string) => {
    setRequests(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const getActiveRequests = useCallback(() => {
    return Array.from(requests.values());
  }, [requests]);

  const getBlockingRequests = useCallback(() => {
    return Array.from(requests.values())
      .filter(req => req.isBlocking)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [requests]);

  const getSmallRequests = useCallback(() => {
    return Array.from(requests.values())
      .filter(req => !req.isBlocking)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }, [requests]);

  const contextValue = useMemo<RequestContextValue>(() => ({
    trackRequest,
    dismissRequest,
    getActiveRequests,
    getBlockingRequests,
    getSmallRequests,
  }), [
    trackRequest,
    dismissRequest,
    getActiveRequests,
    getBlockingRequests,
    getSmallRequests,
  ]);
  RequestManagerContextRef.current = contextValue;

  return (
    <RequestContext.Provider value={contextValue}>
      {children}
    </RequestContext.Provider>
  );
};

// Small request item component
const SmallRequestItem: React.FC<{
  request: RequestState;
}> = ({ request }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { dismissRequest } = useRequestManager();

  const onExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const getBackgroundColor = () => {
    if (request.isCompleted) return "bg-green-500";
    if (request.errors && request.errors.length > 0) return "bg-red-500";
    return "bg-yellow-400";
  };

  return (
    <div
      className={cx(
        "flex flex-col items-end rounded-md text-black transition-all duration-500",
        getBackgroundColor()
      )}
      onClick={request.errors && request.errors.length > 0 ? onExpand : undefined}
    >
      <div className="flex items-center gap-2 py-2 px-4 justify-end gap-2">
        {request.isLoading && (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
        )}
        {request.isCompleted && (
          <MdCheck size={24} className="text-white" />
        )}
        {request.errors && (
          <MdError size={24} className="text-black" />
        )}
        <span className="text-lg truncate">
          {request.title || 'Loading...'}
        </span>
        
        <button
          onClick={() => dismissRequest(request.id)}
          className="text-black"
        >
          <MdClose size={24} />
        </button>
      </div>
      {/* <button
        onClick={() => dismissRequest(request.id)}
        className="text-blue-600 hover:text-blue-800 p-1 ml-auto"
      >
        <MdClose size={16} />
      </button> */}

      {isExpanded && request.errors && request.errors.length > 0 && (
        <div className="py-2 px-4 bg-white">
          {request.errors.map(error => (
            <div key={error.message}>
              <div className="text-sm text-red-700 mb-2 font-bold">
                {error.status}:
              </div>
              <div className="text-md text-red-700 mb-2 font-bold">
                {error.message}
              </div>
              {error.debugInfo && (
                <div className="text-sm text-red-700 mb-2">
                  {JSON.stringify(error.debugInfo, null, 2)}
                </div>
              )}
              {error.stack && (
                <pre className="text-sm text-red-700 mb-2">
                  {error.stack.map((line: string) => <span key={line}>{line}<br /></span>)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Blocking request overlay component
const BlockingRequestOverlay: React.FC<{ request: RequestState }> = ({ request }) => {
  const { dismissRequest } = useRequestManager();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 10000 }}>
      <div className="rounded-lg bg-white p-8 shadow-xl mx-4 flex flex-col items-center gap-4 border-2 transition-all duration-500">
        {request.isLoading && (
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
        )}
        {request.isCompleted && (
          <MdCheck size={48} className="text-green-500 animate-pulse" />
        )}
        {request.errors && (
          <MdError size={30} className="text-red-500" />
        )}
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">
          {request.title || 'Processing...'}
        </h3>
        {request.isLoading && (
          <p className="text-gray-600">
            Please wait while we complete this operation.
          </p>
        )}
        {request.isCompleted && (
          <p className="text-green-600 font-medium">
            Operation completed successfully!
          </p>
        )}

        {request.errors && request.errors.map(error => (
          <div key={error.message}>
            <div className="text-md text-red-700 mb-2 font-bold">
              {error.status}
            </div>
            <div className="text-md text-red-700 mb-2 font-bold">
              {error.message}
            </div>
            {error.debugInfo && (
              <div className="text-sm text-red-700 mb-2">
                {JSON.stringify(error.debugInfo, null, 2)}
              </div>
            )}
            {error.stack && (
              <pre className="text-sm text-red-700 mb-2">
                {error.stack.map((line: string) => <span key={line}>{line}<br /></span>)}
              </pre>
            )}
          </div>
        ))}

        <Button onClick={() => dismissRequest(request.id)} preset='white' size='sm'>
          Ignorer
        </Button>
      </div>
    </div>
  );
};

// Main RequestManager component
export const RequestManager: React.FC = () => {
  const { getBlockingRequests, getSmallRequests } = useRequestManager();

  const blockingRequests = getBlockingRequests();
  const smallRequests = getSmallRequests();

  // Show blocking overlay if there are blocking requests
  if (blockingRequests.length > 0) {
    const request = blockingRequests[0];
    
    // For completed blocking requests, we still show the overlay
    // The setTimeout in trackRequest will remove it after 2 seconds
    return <BlockingRequestOverlay request={request} />;
  }

  // Show small requests if any exist
  if (smallRequests.length > 0) {
    return (
      <div className="fixed top-20 right-4 space-y-2" style={{ zIndex: 10000 }}>
        {smallRequests.map(request => (
          <SmallRequestItem
            key={request.id}
            request={request}
          />
        ))}
      </div>
    );
  }

  return null;
};

export default RequestManager;
